/**
 * ThreeModes — pitch the puzzle/typing/speaking trinity with a card each.
 * Card icons come from Lucide (matching what the user sees in the app nav).
 */
import { GlassButton } from '@/components/GlassButton';
import { Icon, type IconName } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';

interface Mode {
  icon: IconName;
  title: string;
  desc: string;
  bullets: readonly string[];
  spotlight: `rgba(${number}, ${number}, ${number}, ${number})`;
  to: string;
}

const MODES: readonly Mode[] = [
  {
    icon: 'puzzle',
    title: '单词拼图',
    desc: '拖拽打乱的单词卡片重组句子。像搭积木一样直观感受语法逻辑。',
    bullets: ['可视化句子结构', '错位时高亮提示', 'L0→L2 难度自动升级'],
    spotlight: 'rgba(0, 240, 255, 0.28)',
    to: '/app/study/puzzle/l-beg-1-1',
  },
  {
    icon: 'typing',
    title: '键盘打字',
    desc: '字符级 diff + WPM 监测。每个按键都有 click 音，错字立刻 thunk。',
    bullets: ['字符级实时校验', '键盘音效', 'WPM + 错误数 HUD'],
    spotlight: 'rgba(170, 255, 0, 0.28)',
    to: '/app/study/typing/l-beg-1-1',
  },
  {
    icon: 'speaking',
    title: '语音口语',
    desc: '语音识别 0-100 分。90+ 触发 Perfect 烟花，60- 自动入错题。',
    bullets: ['音节级评分', '元音 / 重音诊断', 'AI 朗读对照'],
    spotlight: 'rgba(255, 46, 136, 0.28)',
    to: '/app/study/speaking/l-beg-1-1',
  },
];

export function HomeThreeModes(): JSX.Element {
  return (
    <section className="relative py-24 md:py-32 border-t border-honey-400/10">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Three Modes · 三重输入"
            title={<>同一句话 · <span className="text-gradient-warm">看 · 写 · 说</span> 三遍</>}
            subtitle={'L0 看着学 → L1 自己组 → L2 凭空说。同一句话被你"杀" 3 次，才能真正变成肌肉记忆。'}
          />
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {MODES.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.08}>
              <SpotlightCard spotlightColor={m.spotlight} className="h-full">
                <Icon name={m.icon} size="xl" className="mb-4 text-honey-400" />
                <h3 className="font-display text-2xl font-bold text-white">{m.title}</h3>
                <p className="mt-3 text-sm text-cream-300/90 leading-relaxed">{m.desc}</p>
                <ul className="mt-5 space-y-2 text-sm text-cream-300">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <span className="h-1 w-1 rounded-full bg-honey-400" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <GlassButton to={m.to} size="sm" variant="solid">
                    试一下 →
                  </GlassButton>
                </div>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
